<template>
    <div class="notification-chatbot-wrapper">

        <!-- Notification List -->
        <div v-if="msg.type === 'view_notifications_list'"
             class="notification-card">

            <!-- Header -->
            <div class="notification-header">
                <div class="notification-header-text">
                    <h4>{{ notificationData.heading || 'Notifications' }}</h4>

                    <p>
                        {{ unreadTotal }} unread � {{ readTotal }} read in {{ notifications.length }} loaded
                        � {{ totalCount }} total
                    </p>
                </div>
            </div>

            <!-- Filters -->
            <div class="notification-filter-row">

                <button type="button"
                        :class="[
                        'notification-filter-btn',
                        notificationFilter === 'all' ? 'active' : ''
                    ]"
                        @click="setNotificationFilter('all')">

                    All
                </button>

                <button type="button"
                        :class="[
                        'notification-filter-btn',
                        notificationFilter === 'unread' ? 'active' : ''
                    ]"
                        @click="setNotificationFilter('unread')">

                    Unread
                </button>

                <button type="button"
                        :class="[
                        'notification-filter-btn',
                        notificationFilter === 'read' ? 'active' : ''
                    ]"
                        @click="setNotificationFilter('read')">

                    Read
                </button>

            </div>

            <!-- Notifications -->
            <div v-if="filteredNotifications.length"
                 ref="notificationBody"
                 class="notification-body"
                 @scroll="handleNotificationScroll">

                <div v-for="(item, index) in visibleNotifications"
                     :key="item.notificationId || item.id || index"
                     class="notification-item">

                    <div class="notification-content">

                        <!-- Read / Unread -->
                        <div class="notification-item-top notification-item-top-right">

                            <span :class="[
                                    'notification-status-badge',
                                    isRead(item)
                                        ? 'notification-read-badge'
                                        : 'notification-unread-badge'
                                ]">

                                {{ isRead(item) ? 'Read' : 'Unread' }}

                            </span>

                        </div>

                        <!-- Message -->
                        <div class="notification-message">
                            {{ item.message || '' }}
                        </div>

                        <!-- Created Date -->
                        <div v-if="item.createdOn"
                             class="notification-date">

                            {{ item.createdOn }}

                        </div>
                    </div>
                </div>

                <!-- Load More -->
                <div v-if="hasMoreNotifications"
                     class="notification-load-more">

                    <button type="button"
                            class="notification-load-more-btn"
                            :disabled="loadingMore"
                            @click="loadMoreNotifications">

                        {{
                            loadingMore
                                ? 'Loading...'
                                : 'Load More Notifications'
                        }}

                    </button>

                </div>

                <!-- All Loaded -->
                <div v-else-if="
                        reachedBottom &&
                        !hasMoreNotifications &&
                        visibleNotifications.length
                    "
                     class="notification-end-text">

                    All matching notifications loaded.

                </div>

            </div>

            <!-- Empty after filter -->
            <div v-else
                 class="notification-empty">

                <template v-if="notifications.length < totalCount">
                    No matching notifications in the records loaded so far.
                </template>
                <template v-else-if="notificationFilter === 'unread'">
                    No unread notifications found.
                </template>

                <template v-else-if="notificationFilter === 'read'">
                    No read notifications found.
                </template>

                <template v-else>
                    No notifications found.
                </template>

                <div v-if="hasMoreNotifications" class="notification-load-more">
                    <button type="button" class="notification-load-more-btn"
                            :disabled="loadingMore" @click="loadMoreNotifications">
                        {{ loadingMore ? 'Loading...' : 'Load More Notifications' }}
                    </button>
                </div>

            </div>

        </div>

        <!-- Empty -->
        <div v-else-if="msg.type === 'view_notifications_empty'"
             class="notification-text-card">

            {{ responseText || 'No notifications found.' }}

        </div>

        <!-- Failed -->
        <div v-else-if="msg.type === 'view_notifications_failed'"
             class="notification-text-card notification-error">

            {{ responseText || 'Unable to load notifications.' }}

        </div>

        <!-- Generic -->
        <div v-else
             class="notification-text-card">

            {{ responseText }}

        </div>

    </div>
</template>


<script>
    import UserService from '@/service/user.service.js';

    import { flutterBridgeMixin } from '../flutterBridgeMixin';

    export default {
        name: 'NotificationsChatbot',
        mixins: [flutterBridgeMixin],

        props: {
            msg: {
                type: Object,
                required: true,
            },

            disabled: {
                type: Boolean,
                default: false,
            },

            currentDate: {
                type: String,
                default: '',
            },

            loadingMore: {
                type: Boolean,
                default: false,
            },
        },

        emits: [
            'load-more',
        ],

        data() {
            return {
                notificationFilter: 'all',
                notificationPageSize: 15,
                notificationVisibleCount: 15,
                reachedBottom: false,
                showLoadMore: false,

                // Keeps all pages together when Load More returns another page.
                loadedNotifications: [],

                // Exact unread count from the same portal API.
                // null keeps the existing chatbot count as a safe fallback.
                portalUnreadCount: null,
            };
        },

        mounted() {
            this.loadPortalUnreadCount();
        },

        computed: {

            payload() {
                return this.msg?.payload || {};
            },

            notificationData() {
                return this.payload?.notification_data || {};
            },

            /*
             * Notifications from the current backend response.
             */
            rawNotifications() {
                const data = this.notificationData;

                if (Array.isArray(data)) return data;
                if (Array.isArray(data.notifications)) return data.notifications;
                if (Array.isArray(data.data)) return data.data;
                if (Array.isArray(data.items)) return data.items;
                if (Array.isArray(data.result)) return data.result;

                return [];
            },

            /*
             * Combined notifications from the first response + Load More pages.
             */
            notifications() {
                return this.loadedNotifications.length
                    ? this.loadedNotifications
                    : this.rawNotifications;
            },

            totalCount() {
                return Number(
                    this.notificationData.totalCount ||
                    this.notifications.length ||
                    0
                );
            },

            unreadTotal() {
                // These counts describe loaded records, not an inferred server total.
                return this.notifications.filter(
                    item => !this.isRead(item)
                ).length;
            },

            readTotal() {
                return this.notifications.filter(item => this.isRead(item)).length;
            },

            /*
             * All / Unread / Read filter.
             */
            filteredNotifications() {

                if (this.notificationFilter === 'unread') {

                    return this.notifications.filter(
                        item => !this.isRead(item)
                    );
                }

                if (this.notificationFilter === 'read') {

                    return this.notifications.filter(
                        item => this.isRead(item)
                    );
                }

                return this.notifications;
            },

            /*
             * Only first 15 initially.
             */
            visibleNotifications() {
                return this.filteredNotifications.slice(
                    0,
                    this.notificationVisibleCount
                );
            },

            /*
             * More records exist either:
             *
             * 1. In current backend response
             * 2. On backend because totalCount > loaded records
             */
            hasMoreNotifications() {

                const moreLocal =
                    this.notificationVisibleCount <
                    this.filteredNotifications.length;

                // Fetch unfiltered pages and apply the selected filter locally.
                const moreOnServer =
                    this.notifications.length < this.totalCount;

                return moreLocal || moreOnServer;
            },

            responseText() {
                return (
                    this.payload?.response ||
                    this.msg?.text ||
                    ''
                );
            },
        },

        watch: {
            rawNotifications: {
                immediate: true,
                deep: true,
                handler(list) {
                    const incoming = Array.isArray(list) ? list : [];
                    if (!incoming.length) return;

                    const previousLength = this.loadedNotifications.length;
                    const wasFullyVisible =
                        previousLength === 0 ||
                        this.notificationVisibleCount >= previousLength;

                    const merged = [...this.loadedNotifications];
                    const indexById = new Map();

                    merged.forEach((item, index) => {
                        const id =
                            item?.notificationId ||
                            item?.NotificationId ||
                            item?.id;

                        if (id) {
                            indexById.set(String(id), index);
                        }
                    });

                    incoming.forEach((item) => {
                        const id =
                            item?.notificationId ||
                            item?.NotificationId ||
                            item?.id;

                        if (id && indexById.has(String(id))) {
                            merged[indexById.get(String(id))] = item;
                        } else {
                            if (id) {
                                indexById.set(String(id), merged.length);
                            }
                            merged.push(item);
                        }
                    });

                    this.loadedNotifications = merged;

                    if (
                        merged.length > previousLength &&
                        wasFullyVisible &&
                        previousLength > 0
                    ) {
                        this.notificationVisibleCount = Math.min(
                            merged.length,
                            this.notificationVisibleCount + this.notificationPageSize
                        );
                    }

                    this.$nextTick(() => {
                        const el = this.$refs.notificationBody;
                        if (el) {
                            this.handleNotificationScroll({ target: el });
                        }
                    });
                },
            },
        },

        methods: {

            parsePortalUnreadCount(payload) {
                if (payload === null || payload === undefined) {
                    return null;
                }

                if (
                    typeof payload === 'number' &&
                    Number.isFinite(payload)
                ) {
                    return payload;
                }

                if (
                    typeof payload === 'string' &&
                    payload.trim() !== '' &&
                    Number.isFinite(Number(payload))
                ) {
                    return Number(payload);
                }

                if (typeof payload !== 'object') {
                    return null;
                }

                const keys = [
                    'unreadCount',
                    'UnreadCount',
                    'count',
                    'Count',
                    'total',
                    'Total',
                    'totalCount',
                    'TotalCount',
                ];

                for (const key of keys) {
                    const value = payload[key];

                    if (
                        value !== undefined &&
                        value !== null &&
                        value !== '' &&
                        Number.isFinite(Number(value))
                    ) {
                        return Number(value);
                    }
                }

                return null;
            },

            async loadPortalUnreadCount() {
                if (this.msg?.type !== 'view_notifications_list') {
                    return;
                }

                const userId = String(
                    localStorage.getItem('userId') || ''
                ).trim();

                if (!userId) {
                    this.portalUnreadCount = null;
                    return;
                }

                try {
                    /*
                     * Same portal endpoint already exposed by UserService:
                     * ManageDashBoard/getunreadcount/{userId}
                     */
                    const response =
                        await UserService.getUnreadNotification(userId);

                    const body = response?.data;

                    const payload =
                        body?.result ??
                        body?.Result ??
                        body?.data ??
                        body?.Data ??
                        body;

                    const count =
                        this.parsePortalUnreadCount(payload);

                    this.portalUnreadCount =
                        count !== null ? count : null;
                } catch (error) {
                    // Do not affect existing notification functionality.
                    // Fall back to notificationData.unreadCount.
                    this.portalUnreadCount = null;

                    console.error(
                        'Portal unread notification count load failed:',
                        error
                    );
                }
            },

            isRead(item) {
                if (!item) return false;

                const value = item?.isRead ?? item?.IsRead ?? item?.read;
                return value === true || value === 1 ||
                    ['true', '1'].includes(String(value ?? '').trim().toLowerCase());
            },

            setNotificationFilter(filter) {

                this.notificationFilter = filter;

                /*
                 * Reset to first 15 whenever filter changes.
                 */
                this.notificationVisibleCount = 15;

                this.reachedBottom = false;

                this.showLoadMore = false;

                this.$nextTick(() => {

                    const el = this.$refs.notificationBody;

                    if (el) {
                        el.scrollTop = 0;
                    }
                });
            },

            handleNotificationScroll(event) {

                const element = event?.target;

                if (!element) return;

                const distanceFromBottom =
                    element.scrollHeight -
                    element.scrollTop -
                    element.clientHeight;

                const isBottom =
                    distanceFromBottom <= 10;

                this.reachedBottom = isBottom;

                this.showLoadMore =
                    isBottom &&
                    this.hasMoreNotifications;
            },

            loadMoreNotifications() {
                if (this.loadingMore) return;

                // STEP 1:
                // There are already more notifications available locally.
                // Example: API returned 20, UI currently shows 15.
                if (
                    this.notificationVisibleCount <
                    this.filteredNotifications.length
                ) {
                    this.notificationVisibleCount = Math.min(
                        this.notificationVisibleCount + this.notificationPageSize,
                        this.filteredNotifications.length
                    );

                    this.reachedBottom = false;
                    this.showLoadMore = false;

                    this.$nextTick(() => {
                        const el = this.$refs.notificationBody;

                        if (el) {
                            // Keep Load More available if required.
                            const distanceFromBottom =
                                el.scrollHeight -
                                el.scrollTop -
                                el.clientHeight;

                            if (distanceFromBottom <= 10) {
                                this.reachedBottom = true;
                                this.showLoadMore =
                                    this.hasMoreNotifications;
                            }
                        }
                    });

                    return;
                }

                // STEP 2:
                // All currently received notifications are displayed.
                // Backend still has more records.
                if (
                    this.notifications.length < this.totalCount
                ) {
                    const nextPage =
                        Math.floor(
                            this.notifications.length /
                            this.notificationPageSize
                        ) + 1;

                    this.reachedBottom = false;
                    this.showLoadMore = false;

                    this.$emit('load-more', {
                        pageNumber: nextPage,
                        pageSize: this.notificationPageSize,
                        filter: 'all',
                        loadedCount: this.notifications.length,
                        totalCount: this.totalCount,
                    });
                }
            },
        },
    };
</script>


<style scoped>

    .notification-chatbot-wrapper {
        width: 100%;
        max-width: 330px;
        font-family: Outfit, sans-serif;
        box-sizing: border-box;
    }


    /* ================================
       CARD
    ================================ */

    .notification-card {
        width: 100%;
        max-width: 330px;
        background: #ffffff;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        box-sizing: border-box;
    }


    /* ================================
       HEADER
    ================================ */

    .notification-header {
        padding: 14px 16px;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
    }

    .notification-header-text {
        min-width: 0;
    }

    .notification-header h4 {
        margin: 0;
        font-size: 14px;
        line-height: 1.3;
        font-weight: 700;
    }

    .notification-header p {
        margin: 4px 0 0;
        font-size: 11px;
        line-height: 1.4;
        opacity: 0.9;
    }

    /* ================================
       FILTERS
    ================================ */

    .notification-filter-row {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 14px;
        background: #ffffff;
        border-bottom: 1px solid #edf0f4;
    }

    .notification-filter-btn {
        padding: 5px 11px;
        border: 1px solid #d7dce2;
        border-radius: 14px;
        background: #ffffff;
        color: #475467;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
    }

        .notification-filter-btn:hover {
            border-color: #0f66b8;
            color: #0f66b8;
        }

        .notification-filter-btn.active {
            background: #0f66b8;
            border-color: #0f66b8;
            color: #ffffff;
        }


    /* ================================
       BODY
    ================================ */

    .notification-body {
        max-height: 350px;
        overflow-y: auto;
        padding: 4px 14px 10px;
        box-sizing: border-box;
    }

        .notification-body::-webkit-scrollbar {
            width: 5px;
        }

        .notification-body::-webkit-scrollbar-track {
            background: transparent;
        }

        .notification-body::-webkit-scrollbar-thumb {
            background: #c7d0dc;
            border-radius: 10px;
        }


    /* ================================
       ITEM
    ================================ */

    .notification-item {
        padding: 12px 0;
        border-bottom: 1px solid #edf0f4;
    }

        .notification-item:last-child {
            border-bottom: 0;
        }

    .notification-content {
        min-width: 0;
    }

    .notification-item-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
    }

    .notification-item-top-right {
        justify-content: flex-end;
    }


    /* ================================
       STATUS BADGES
    ================================ */

    .notification-status-badge {
        flex: 0 0 auto;
        padding: 3px 8px;
        border-radius: 10px;
        font-size: 9px;
        font-weight: 700;
    }

    .notification-unread-badge {
        background: #e8f2ff;
        color: #0f66b8;
    }

    .notification-read-badge {
        background: #f1f3f5;
        color: #667085;
    }


    /* ================================
       MESSAGE
    ================================ */

    .notification-message {
        margin-top: 5px;
        color: #596273;
        font-size: 11px;
        line-height: 1.5;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    .notification-date {
        margin-top: 5px;
        color: #9299a3;
        font-size: 10px;
        line-height: 1.3;
    }

    /* ================================
       LOAD MORE
    ================================ */

    .notification-load-more {
        padding: 12px 0 6px;
        text-align: center;
    }

    .notification-load-more-btn {
        padding: 8px 14px;
        border: none;
        border-radius: 8px;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
    }

        .notification-load-more-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

    .notification-end-text {
        padding: 10px 4px 5px;
        color: #98a2b3;
        font-size: 10px;
        text-align: center;
    }


    /* ================================
       EMPTY
    ================================ */

    .notification-empty {
        padding: 24px 16px;
        color: #8b93a1;
        font-size: 12px;
        text-align: center;
    }


    /* ================================
       TEXT
    ================================ */

    .notification-text-card {
        width: fit-content;
        min-width: 0;
        max-width: 330px;
        padding: 12px 16px;
        background: #ffffff;
        color: #333333;
        border-radius: 6px 16px 16px 16px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        box-sizing: border-box;
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    .notification-error {
        color: #b42318;
    }


    /* ================================
       MOBILE
    ================================ */

    @media (max-width: 480px) {

        .notification-chatbot-wrapper,
        .notification-card,
        .notification-text-card {
            max-width: 300px;
        }

        .notification-body {
            max-height: 320px;
            padding-left: 12px;
            padding-right: 12px;
        }

        .notification-filter-row {
            padding-left: 12px;
            padding-right: 12px;
        }
    }
</style>
